import { Injectable } from '@angular/core';

import {
    IField, IAdvancedSearchData, IAzavistaGroupedFieldsComponentData, IAutocompleteInputFieldValue, IRowAction, FieldComponentType,
    IObjectEnumInputFieldValue, IIdWithLabel, ITableSettingsChangedData, IIdWithName
} from '@azavista/components/shared';
import {
    IEvent, AzavistaApiService, IBulkSetGlobalEventFieldRequest, EventType, EventPublish, ThemeType, EventAccess,
    ITeamListItem,
    ISearchIntegrationsRequest,
    ISearchPagesRequest,
    PageType,
    PageSubtype
} from '@azavista/servicelib';
import { EventRowActionId, EventTemplateRowActionId } from './enums';
import {
    AclService, EventPresetType, IColumnsListSetting, IconName, RequiredScopesService,
    SettingEntityType, SharedService, TRANSLATION_MAP, TranslationKey
} from '../shared';
import { SettingsService } from '../shared/settings.service';
import { IConfirmationDialogComponentData } from '../shared/confirmation-dialog/confirmation-dialog.interfaces';
import { AzavistaUpdateFieldValueService, IUpdateFieldData } from '@azavista/components/update-field-value';
import { SearchParams } from '@azavista/advanced-search';
import { EVENT_PRESETS_ITEMS } from './select-event-preset-popup/select-event-preset-popup.component';
import { reduce } from 'rxjs';
import { CreateEventFromPresetEventData } from './list/utils';

export const EVENT_COUNTER_TOTAL_FIELD_NAME = '__eventCounter.total';

@Injectable({
    providedIn: 'root'
})
export class EventsService {
    globalEventFields: IField[];

    private readonly settingsAccessFieldId = 'settings.access';
    private readonly settingsPublishedFieldId = 'settings.publish';
    private readonly settingsLastRegistrationDateFieldId = 'settings.last_registration_date';
    private readonly settingsPublicationDateFieldId = 'settings.publication_date';
    private readonly settingsOfflineDateFieldId = 'settings.offline_date';
    private readonly eventCounterTotalFieldName = EVENT_COUNTER_TOTAL_FIELD_NAME;
    private readonly eventCounterRegisteredFieldName = '__eventCounter.registered';
    private readonly eventCounterArrivedFieldName = '__eventCounter.arrived';
    private readonly eventCounterCancelledFieldName = '__eventCounter.cancelled';

    constructor(
        private apiSvc: AzavistaApiService, private aclSvc: AclService, private rsSvc: RequiredScopesService,
        private settingsSvc: SettingsService, private sharedSvc: SharedService, private updateFieldSvc: AzavistaUpdateFieldValueService
    ) {
    }

    async processEventCreationFailed(eventId: string): Promise<void> {
        await this.apiSvc.deleteEvent(eventId);
        await this.sharedSvc.showConfirmationDialog(
            this.sharedSvc.translate(TranslationKey.jobFailed),
            [this.sharedSvc.translate(TranslationKey.creationOfTheEventDidNotCompleteSuccessfullyAndItWasDeleted)],
            true
        );
    }

    async processUpdateEventField(
        eventIds: string[], allTableItemsSelected: boolean, tableSettingsChangedData: ITableSettingsChangedData,
        eventType: EventType, selectedItemsCount: number
    ): Promise<string> {
        const allEventFields: IField[] = await this.apiSvc.getAllGlobalEventFields()
            .then(fields => fields.map(field => ({ ...field, editable: true })));

        const data: IUpdateFieldData = {
            fields: allEventFields,
            selectedField: null,
            selectedValue: null,
            selectedItemsCountData: {
                selectedCount: selectedItemsCount, allSelected: false, totalCount: selectedItemsCount
            }
        };
        const result = await this.updateFieldSvc.showUpdateFieldDialog(data);
        if (result && result.selectedField) {
            const filter = this.sharedSvc.createAdvancedSearchRequestFromTableWithoutSorting(
                allTableItemsSelected, eventIds, tableSettingsChangedData
            );
            const req: IBulkSetGlobalEventFieldRequest = {
                event_filter: filter,
                value: result.selectedValue.value
            };
            const startJobRespone = await this.apiSvc.bulkUpdateGlobalEventField(result.selectedField.name, 'name', eventType, req);
            return startJobRespone.job_id;
        } else {
            return '';
        }
    }

    async loadGlobalEventFields(): Promise<IField[]> {
        if (this.globalEventFields) {
            return this.globalEventFields;
        }
        this.globalEventFields = await this.apiSvc.getAllGlobalEventFields();
        this.globalEventFields.forEach(x => x.editable = true);
        return this.globalEventFields;
    }


    async showArchiveEventConfirmationDialog(selectedItemsCount?: number): Promise<IConfirmationDialogComponentData> {
        return this.sharedSvc.showConfirmationDialog(
            this.sharedSvc.translate(TranslationKey.archiveEventsDialogTitle),
            [
                this.sharedSvc.translate(TranslationKey.archiveEventsDialogAreYouSure),
                this.sharedSvc.translate(TRANSLATION_MAP.ARCHIVED_EVENTS_CAN_BE_RETRIEVED_IN_THE_ARCHIVED_EVENTS_SECTION)
            ],
            false,
            selectedItemsCount ? {
                selectedCount: selectedItemsCount, allSelected: false, totalCount: selectedItemsCount
            } : null
        );
    }

    async showArchiveWorkflowConfirmationDialog(selectedItemsCount?: number): Promise<IConfirmationDialogComponentData> {
        return this.sharedSvc.showConfirmationDialog(
            this.sharedSvc.translate(TranslationKey.archiveWorkflowsDialogTitle),
            [
                this.sharedSvc.translate(TranslationKey.archiveWorkflowsDialogAreYouSure),
                this.sharedSvc.translate(TranslationKey.wontBeAbleToRestoreData)
            ],
            false,
            selectedItemsCount ? {
                selectedCount: selectedItemsCount, allSelected: false, totalCount: selectedItemsCount
            } : null
        );
    }

    async showArchiveEventTemplateConfirmationDialog(): Promise<IConfirmationDialogComponentData> {
        return this.sharedSvc.showConfirmationDialog(
            this.sharedSvc.translate(TranslationKey.archiveEventTemplateDialogTitle),
            [
                this.sharedSvc.translate(TranslationKey.archiveEventTemplateDialogAreYouSure),
                this.sharedSvc.translate(TRANSLATION_MAP.ARCHIVED_EVENT_TEMPLATES_CAN_BE_RETRIEVED_IN_THE_ARCHIVED_EVENTS_SECTION)
            ]
        );
    }

    clearCachedData(): void {
        this.globalEventFields = null;
    }

    async getAllUserTeamsWithEventCreateScope(): Promise<ITeamListItem[]> {
        const allTeams = (await this.apiSvc.getTeamsList()).teams;
        const teamIdsWithEventCreateScope = this.aclSvc.getCurrentUserTeamIdsContainingScope(this.rsSvc.getEventCreateScope());
        const teams = allTeams.filter(x => teamIdsWithEventCreateScope.some(y => y === x.id));
        return teams;
    }

    async createEventFromPredefinedGroupData(eventPresetType: EventPresetType, options?: {
        skipWebsiteConfig?: boolean,
        skipEmailConfig?: boolean,
        skipPaymentConfig?: boolean,
        disabledFieldNames?: Array<keyof CreateEventFromPresetEventData>,
    }): Promise<IAzavistaGroupedFieldsComponentData> {
        const globalEventFields = await this.loadGlobalEventFields();
        const category = this.sharedSvc.translate(TRANSLATION_MAP.EVENT_DETAILS);
        const nameField = {
            id: 'name', name: 'name', label: this.sharedSvc.translate(TranslationKey.name),
            category,
            schema: { type: 'string' }, editable: true, mandatory_for_planners: true
        } as IField;
        const startDateField = {
            id: 'start', name: 'start', label: this.sharedSvc.translate(TranslationKey.start),
            category,
            schema: { type: 'string', format: 'date-time' }, editable: true, mandatory_for_planners: true
        } as IField;
        const endDateField = {
            id: 'end', name: 'end', label: this.sharedSvc.translate(TranslationKey.end),
            category,
            schema: { type: 'string', format: 'date-time' }, editable: true, mandatory_for_planners: true
        } as IField;
        const timezoneField = this.sharedSvc.clone(globalEventFields.find(x => x.name === 'timezone'));
        timezoneField.mandatory_for_planners = true;
        timezoneField.category = startDateField.category;
        const teamField = {
            id: 'team', name: 'team', label: this.sharedSvc.translate(TranslationKey.team),
            category,
            schema: { type: 'object' }, editable: true, component: FieldComponentType.objectEnum, mandatory_for_planners: true
        } as IField;
        const allTeams = (await this.apiSvc.getTeamsList()).teams;
        const teamFieldValue: IObjectEnumInputFieldValue = {
            items: allTeams, displayProperty: 'name', selected: null
        };
        const typeField = {
            id: 'type', name: 'type', label: this.sharedSvc.translate(TranslationKey.type),
            category,
            schema: { type: 'object' }, editable: false, component: FieldComponentType.objectEnum, mandatory_for_planners: true
        } as IField;
        const typeItems: IIdWithLabel[] = EVENT_PRESETS_ITEMS.map(item => ({
            id: item.type,
            label: this.sharedSvc.translate(item.label),
        } as IIdWithLabel));
        const typeFieldValue: IObjectEnumInputFieldValue = {
            items: typeItems, displayProperty: 'label', selected: typeItems.find(x => x.id === eventPresetType)
        };
        const entity = {
            name: '',
            start: '',
            end: '',
            timezone: this.apiSvc.getCurrentUser().timezone,
            type: typeFieldValue,
            team: teamFieldValue,
            fromEmail: '',
            bounceEmail: ''
        } as any;
        const result: IAzavistaGroupedFieldsComponentData = {
            expandPanels: true,
            editModeForAllFields: true,
            singleColumn: true,
            showActionButtons: true,
            disableAutoOrdering: true,
            fieldsWithEntity: {
                entity: entity,
                fields: [
                    typeField, nameField, startDateField, endDateField, timezoneField,
                    teamField
                ]
            }
        };
        if (!options?.skipWebsiteConfig) {
            const websiteCategory = this.sharedSvc.translate(TRANSLATION_MAP.WEBSITE);
            const themeField = {
                id: 'theme', name: 'theme', label: this.sharedSvc.translate(TranslationKey.theme),
                category: websiteCategory,
                schema: { type: 'object' }, editable: true, component: FieldComponentType.objectEnum, mandatory_for_planners: true
            } as IField;
            const allThemes = await this.apiSvc.getAllThemes(ThemeType.website);
            const themeFieldValue: IObjectEnumInputFieldValue = {
                items: allThemes, displayProperty: 'name', selected: null
            };
            entity.theme = themeFieldValue;
            const headerAndFooterPages = await this.apiSvc.getAllPagedItems(
                (req) => {
                    const searchPageReq: ISearchPagesRequest = {
                        ...req,
                        pageType: PageType.theme
                    };
                    return this.apiSvc.searchPages(searchPageReq);
                },
                res => res.pages,
            );
            const {Footer: footerEnums, Header: headerEnums } = headerAndFooterPages.reduce((record, page) => {
                const subType = page.page_subtype as PageSubtype.header | PageSubtype.footer;
                const recordSubtype = record[subType];
                recordSubtype[page.id] = page.name;
                return record;
            }, {[PageSubtype.header]: {} as Record<string, string>, [PageSubtype.footer]: {} as Record<string, string>});
            const footerPageField = this.sharedSvc.createSelectField({
                label: this.sharedSvc.translate(TRANSLATION_MAP.FOOTER),
                name: 'footerPageId',
                category: websiteCategory,
            }, footerEnums);
            const headerPageField = this.sharedSvc.createSelectField({
                label: this.sharedSvc.translate(TRANSLATION_MAP.HEADER),
                name: 'headerPageId',
                category: websiteCategory,
            }, headerEnums);
            result.fieldsWithEntity.fields.push(themeField, headerPageField, footerPageField);
        }
        if (!options?.skipEmailConfig) {
            const emailCategory = this.sharedSvc.translate(TRANSLATION_MAP.EMAIL);
            const emailThemeField = {
                id: 'emailTheme', name: 'emailTheme', label: this.sharedSvc.translate(TranslationKey.emailThemes),
                category: emailCategory,
                schema: { type: 'object' }, editable: true, component: FieldComponentType.objectEnum, mandatory_for_planners: true
            } as IField;
            const allEmailThemes = await this.apiSvc.getAllThemes(ThemeType.email);
            const emailThemeFieldValue: IObjectEnumInputFieldValue = {
                items: allEmailThemes, displayProperty: 'name', selected: null
            };
            const fromEmailField = {
                id: 'fromEmail', name: 'fromEmail', label: this.sharedSvc.translate(TranslationKey.fromEmailAddress),
                category: emailCategory,
                schema: { type: 'string', format: 'email' }, editable: true, mandatory_for_planners: true
            } as IField;
            const fromEmailNameField = {
                id: 'fromEmailName', name: 'fromEmailName', label: this.sharedSvc.translate(TRANSLATION_MAP.FROM_NAME),
                category: emailCategory,
                schema: { type: 'string',}, editable: true, mandatory_for_planners: true,
            } as IField;
            const replyEmailField = {
                id: 'replyEmail', name: 'replyEmail', label: this.sharedSvc.translate(TRANSLATION_MAP.REPLY_EMAIL),
                category: emailCategory,
                schema: { type: 'string', format: 'email' }, editable: true,
            } as IField;
            const bounceEmailField = {
                id: 'bounceEmail', name: 'bounceEmail', label: this.sharedSvc.translate(TranslationKey.bounceEmail),
                category: emailCategory,
                schema: { type: 'string', format: 'email' }, editable: true, mandatory_for_planners: true
            } as IField;
            result.fieldsWithEntity.fields.push(emailThemeField, fromEmailField, fromEmailNameField, replyEmailField, bounceEmailField);
            entity.emailTheme = emailThemeFieldValue;
        }
        if (!options?.skipPaymentConfig) {
            const paymentCategory = this.sharedSvc.translate(TRANSLATION_MAP.PAYMENT);
            const paymentProviders = await this.apiSvc.getAllPagedItems(
                (req: ISearchIntegrationsRequest) => {
                    req.advancedQuery = { and: [{ type: { eq: 'payment' } }] } as SearchParams;
                    return this.apiSvc.getIntegrationsList(req);
                },
                res => res.integrations
            );
            const paymentIntegrationField = this.sharedSvc.createSelectField({
                label: this.sharedSvc.translate(TRANSLATION_MAP.PAYMENT_PROVIDER),
                name: 'paymentIntegrationId',
                mandatory_for_planners: true,
                category: paymentCategory,
            }, paymentProviders.reduce((record, provider) => {
                return {
                    ...record,
                    [provider.id]: provider.name,
                };
            }, {}));
            result.fieldsWithEntity.fields.push(paymentIntegrationField);
        }
        if (options?.disabledFieldNames) {
            options.disabledFieldNames.forEach(fieldName => {
                const fieldIndex = result.fieldsWithEntity.fields.findIndex(field => field.name === fieldName);
                if (fieldIndex >= 0) {
                    result.fieldsWithEntity.fields.splice(fieldIndex, 1);
                }
            });
        }
        return result;
    }

    async createEventFromTemplateGroupData(): Promise<IAzavistaGroupedFieldsComponentData> {
        const availableTemplates = await this.apiSvc.getAllEvents(EventType.template);
        const templateAutocompleteValue: IAutocompleteInputFieldValue = {
            displayProperty: 'name', items: availableTemplates, filteredItems: availableTemplates, selected: null
        };
        const templateSelector = { __template: templateAutocompleteValue } as any;
        const fields: IField[] = [{
            id: '__template', name: '__template', label: this.sharedSvc.translate(TranslationKey.eventTemplates), category: '',
            schema: { type: 'object' }, component: FieldComponentType.autocomplete, editable: true, builtin: true,
            visible_for_planners: true, mandatory_for_planners: true
        }];
        const result: IAzavistaGroupedFieldsComponentData = {
            expandPanels: true,
            editModeForAllFields: true,
            singleColumn: true,
            showActionButtons: true,
            fieldsWithEntity: {
                entity: templateSelector,
                fields: fields
            },
            disableAutoOrdering: true
        };
        return result;

    }

    async createNewEventGroupData(): Promise<IAzavistaGroupedFieldsComponentData> {
        // TODO: Should we use a constant list of fields or we need to use all builtin/required fields ?
        //       How can we know the relations between fields (like start/end dates and timezone) if they are defined dynamically ?
        const event = {
            name: null,
            start: '',
            end: '',
            timezone: ''
        } as IEvent;
        // We need to add autocomplete type component with available teams
        let availableTeams: ITeamListItem[];
        if (this.aclSvc.isAdmin()) {
            availableTeams = (await this.apiSvc.getTeamsList()).teams;
        } else {
            availableTeams = await this.getAllUserTeamsWithEventCreateScope();
        }
        const teamAutocompleteValue: IAutocompleteInputFieldValue<ITeamListItem> = {
            displayProperty: 'name', items: availableTeams, filteredItems: availableTeams, selected: null
        };
        (event as any).__team = teamAutocompleteValue;

        const fieldsForNewEvent = [...await this.getFieldsForNewEvent()];

        // Convert timezone to autocomplete (for some reason when it is timezone it does not immediatelly show that it is required)
        const timezoneFieldIndex = fieldsForNewEvent.findIndex(x => x.name === 'timezone');
        if (timezoneFieldIndex >= 0) {
            const timezoneField = this.sharedSvc.clone(fieldsForNewEvent[timezoneFieldIndex]);
            timezoneField.component = FieldComponentType.autocomplete;
            const timezonesAsObjects = timezoneField.schema.enum.map(x => ({ id: x, name: x } as IIdWithName));
            const timezoneAutocompleteValue: IAutocompleteInputFieldValue = {
                displayProperty: 'name', items: timezonesAsObjects, filteredItems: timezonesAsObjects, selected: null
            };
            timezoneField.schema = { type: 'object' };
            (event as any).timezone = timezoneAutocompleteValue;
            fieldsForNewEvent[timezoneFieldIndex] = timezoneField;
        }

        const result: IAzavistaGroupedFieldsComponentData = {
            expandPanels: true,
            editModeForAllFields: true,
            singleColumn: true,
            showActionButtons: true,
            fieldsWithEntity: {
                entity: event,
                fields: fieldsForNewEvent
            },
            disableAutoOrdering: true
        };
        return result;
    }

    async getFieldsForNewEvent(): Promise<IField[]> {
        const globalEventFields = await this.loadGlobalEventFields();
        const builtinFields = globalEventFields.filter(x => x.builtin || x.default_field);
        // TODO: We need to have static list of field names and select only them from builtin fields
        //       We also need static list with all required (mandatory_for_planners: true) fields which is a subset of the above list
        const requiredFieldNames = ['name', 'start', 'end', 'timezone'];
        // Mark them as editable so input fields are editable
        builtinFields.forEach(x => {
            x.editable = true;
            if (requiredFieldNames.indexOf(x.name) >= 0) {
                x.mandatory_for_planners = true;
            }
        });
        const fieldsForNewEvent = [...builtinFields];
        // We need to add fabricated team field
        const teamIdField: IField = {
            id: '__team', name: '__team', label: this.sharedSvc.translate(TranslationKey.team), category: builtinFields[0].category,
            schema: { type: 'object' }, component: FieldComponentType.autocomplete, editable: true, builtin: true,
            visible_for_planners: true, mandatory_for_planners: true
        };
        fieldsForNewEvent.push(teamIdField);
        return fieldsForNewEvent;
    }

    getEventTemplatesActions(): IRowAction[] {
        const result: IRowAction[] = [
            {
                label: this.sharedSvc.translate(TranslationKey.viewDetailsMenuAction),
                id: EventTemplateRowActionId.viewDetails, icon: IconName.visibility, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getEventScopesCanRead()
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TranslationKey.createEventMenuAction),
                id: EventTemplateRowActionId.createEvent, icon: IconName.add, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getEventScopesCanCreate()
            },
            {
                label: this.sharedSvc.translate(TRANSLATION_MAP.CREATE_GENERIC_WORKFLOW),
                id: EventTemplateRowActionId.createWorkflow, icon: IconName.settings, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getEventScopesCanCreate()
            },
            {
                label: this.sharedSvc.translate(TranslationKey.duplicateMenuAction),
                id: EventTemplateRowActionId.duplicate, icon: IconName.file_copy, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getEventScopesCanCreate()
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TranslationKey.updateFieldMenuAction),
                id: EventRowActionId.updateField, icon: IconName.refresh, singleSelectionOnly: false,
                requiredScopes: this.rsSvc.getEventScopesCanUpdate()
            },
            {
                label: this.sharedSvc.translate(TRANSLATION_MAP.EXPORT),
                id: EventRowActionId.export, icon: IconName.download, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getEventScopesCanCopy()
            },
            {
                label: this.sharedSvc.translate(TranslationKey.archiveMenuAction),
                id: EventRowActionId.archive, icon: IconName.archive, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getEventScopesCanDelete()
            },
        ];
        return result;
    }

    getWorkflowsActions(): IRowAction[] {
        const result: IRowAction[] = [
            {
                label: this.sharedSvc.translate(TranslationKey.viewDetailsMenuAction),
                id: EventRowActionId.viewDetails, icon: IconName.visibility, singleSelectionOnly: true,
                // No scopes are required to view the event since this is already enforced from search scopes
                // But when the event is shown, it could be read-only if edit is not allowed
                requiredScopes: null
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TranslationKey.duplicateMenuAction),
                id: EventRowActionId.duplicate, icon: IconName.file_copy, singleSelectionOnly: true,
                requiredScopes: null
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TRANSLATION_MAP.EXPORT),
                id: EventRowActionId.export, icon: IconName.download, singleSelectionOnly: true,
                requiredScopes: null
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TranslationKey.archiveMenuAction),
                id: EventRowActionId.archive, icon: IconName.archive, singleSelectionOnly: true,
                requiredScopes: null
            } as IRowAction,
        ];
        return result;
    }

    getEventsActions(): IRowAction[] {
        const result: IRowAction[] = [
            {
                label: this.sharedSvc.translate(TranslationKey.viewDetailsMenuAction),
                id: EventRowActionId.viewDetails, icon: IconName.visibility, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getEventScopesCanRead()
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TRANSLATION_MAP.DUPLICATE),
                id: EventRowActionId.duplicate, icon: IconName.file_copy, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getEventScopesCanCreate()
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TRANSLATION_MAP.EXPORT_4_0),
                id: EventRowActionId.export, icon: IconName.download, singleSelectionOnly: true,
                requiredScopes: this.rsSvc.getEventScopesCanRead()
            } as IRowAction,
            {
                label: this.sharedSvc.translate(TranslationKey.copyAsTemplateMenuAction),
                id: EventRowActionId.copyAsTemplate, icon: IconName.class, singleSelectionOnly: true,
                // TODO: Scopes ?
                requiredScopes: null
            },
            {
                label: this.sharedSvc.translate(TranslationKey.updateFieldMenuAction),
                id: EventRowActionId.updateField, icon: IconName.refresh, singleSelectionOnly: false,
                requiredScopes: this.rsSvc.getEventScopesCanUpdateLocalFields()
            },
            {
                label: this.sharedSvc.translate(TRANSLATION_MAP.EXPORT_DOCUMENTS),
                id: EventRowActionId.bulkExportDocuments, icon: IconName.description, singleSelectionOnly: false,
                requiredScopes: this.rsSvc.getEventScopesCanRead()
            },
            {
                label: this.sharedSvc.translate(TranslationKey.archiveMenuAction),
                id: EventRowActionId.archive, icon: IconName.archive, singleSelectionOnly: false,
                requiredScopes: this.rsSvc.getEventScopesCanDelete()
            }
        ];
        return result;
    }

    getFieldsWithoutSettingsFields(fields: IField[]): IField[] {
        const settingsFieldsIds = [
            this.settingsAccessFieldId, this.settingsLastRegistrationDateFieldId,
            this.settingsOfflineDateFieldId, this.settingsPublicationDateFieldId, this.settingsPublishedFieldId
        ];
        const noSettingsFields = fields.filter(x => !settingsFieldsIds.includes(x.id));
        return noSettingsFields;
    }

    getEventsAdvancedSearchData(columns: IField[]): IAdvancedSearchData {
        const noSettingsFields = this.getFieldsWithoutSettingsFields(columns);
        this.sharedSvc.sortAlphabetically(noSettingsFields, (item) => item.label);
        const result: IAdvancedSearchData = {
            advancedFilters: [],
            allFields: noSettingsFields
        };
        return result;
    }

    getEventTemplatesAdvancedSearchData(columns: IField[]): IAdvancedSearchData {
        const noSettingsFields = this.getFieldsWithoutSettingsFields(columns);
        this.sharedSvc.sortAlphabetically(noSettingsFields, (item) => item.label);
        const result: IAdvancedSearchData = {
            advancedFilters: [],
            allFields: noSettingsFields
        };
        return result;
    }

    async getEventsColumnsSettings(): Promise<IColumnsListSetting> {
        const eventsColumns = this.getEventsColumns(false);
        const allowedIds = eventsColumns.map(x => x.id);
        const defaultIds = this.getBuiltinGlobalFields().map(x => x.id);
        this.addCreatedAtField(defaultIds, eventsColumns);
        const columnsSettings = await this.settingsSvc.getGlobalColumnsSettings(SettingEntityType.event, allowedIds, defaultIds);
        return columnsSettings;
    }

    addCreatedAtField(currentIds: string[], fields: IField[]): void {
        const createdAtField = fields.find(x => x.name === 'created_at');
        if (createdAtField) {
            currentIds.push(createdAtField.id);
        }
    }

    async getEventTemplatesColumnsSettings(): Promise<IColumnsListSetting> {
        const eventTemplatesColumns = this.getEventTemplatesColumns();
        const allowedIds = eventTemplatesColumns.map(x => x.id);
        const defaultIds = this.getBuiltinGlobalFields().map(x => x.id);
        this.addCreatedAtField(defaultIds, eventTemplatesColumns);
        const columnsSettings = await this.settingsSvc.getGlobalColumnsSettings(SettingEntityType.eventTemplate, allowedIds, defaultIds);
        return columnsSettings;
    }

    getCreatedAtField(): IField {
        return {
            id: '__created_at', name: 'created_at', label: this.sharedSvc.translate(TranslationKey.createdAt), editable: false,
            schema: { type: 'string', format: 'date-time' }
        } as IField;
    }

    getEventsColumns(filterUnsupportedColumnFields: boolean): IField[] {
        const teamsNamesField = {
            id: '__teamsNames', name: '__teamsNames', label: this.sharedSvc.translate(TranslationKey.teams),
            editable: false, schema: { type: 'string', enum: [] }
        } as IField;
        const columns: IField[] = [
            ...this.globalEventFields,
            this.getCreatedAtField(),
            ...this.getEventSettingsFields(),
            ...this.getEventCountersFields(),
            teamsNamesField
        ];
        const filteredColumns = filterUnsupportedColumnFields ? this.sharedSvc.getFilteredColumnsForLists(columns) : columns;
        return filteredColumns;
    }

    setEventSettingsFields(events: IEvent[]): void {
        for (const event of events) {
            if (event.settings) {
                const eventAny = event as any;
                eventAny[this.settingsAccessFieldId] = this.translateEventSettingsAccessValue(event.settings.access);
                eventAny[this.settingsLastRegistrationDateFieldId] = event.settings.last_registration_date;
                eventAny[this.settingsOfflineDateFieldId] = event.settings.offline_date;
                eventAny[this.settingsPublicationDateFieldId] = event.settings.publication_date;
                eventAny[this.settingsPublishedFieldId] = this.translateEventSettingsPublishedValue(event.settings.publish);
            }
        }
    }

    setEventCountersFields(events: IEvent[]): void {
        for (const event of events) {
            const eventCounters = event.participant__counts;
            if (eventCounters) {
                const eventAny = event as any;
                eventAny[this.eventCounterArrivedFieldName] = eventCounters.arrived;
                eventAny[this.eventCounterCancelledFieldName] = eventCounters.cancelled;
                eventAny[this.eventCounterRegisteredFieldName] = eventCounters.registered;
                eventAny[this.eventCounterTotalFieldName] = eventCounters.total;
            }
        }
    }

    translateEventSettingsPublishedValue(value: EventPublish): string {
        if (value === EventPublish.NO_REGISTRATION) {
            return this.sharedSvc.translate(TranslationKey.noRegistration);
        } else if (value === EventPublish.PUBLISHED) {
            return this.sharedSvc.translate(TranslationKey.published);
        } else if (value === EventPublish.OFF_LINE) {
            return this.sharedSvc.translate(TranslationKey.offline);
        } else if (value === EventPublish.APPROVED) {
            return this.sharedSvc.translate(TranslationKey.approved);
        } else if (value === EventPublish.CANCELLED) {
            return this.sharedSvc.translate(TranslationKey.cancelled);
        } else if (value === EventPublish.DRAFT) {
            return this.sharedSvc.translate(TranslationKey.draft);
        }
        return value;
    }

    translateEventSettingsAccessValue(value: EventAccess): string {
        if (value === EventAccess.public) {
            return this.sharedSvc.translate(TranslationKey.openToThePublic);
        } else if (value === EventAccess.invited) {
            return this.sharedSvc.translate(TranslationKey.onlyThoseWhoGetInvitation);
        } else if (value === EventAccess.login) {
            return this.sharedSvc.translate(TranslationKey.login);
        }
        return value;
    }

    getEventSettingsFields(): IField[] {
        const settingsFields: IField[] = [];
        settingsFields.push({
            id: this.settingsAccessFieldId, name: this.settingsAccessFieldId, editable: false,
            label: this.sharedSvc.translate(TranslationKey.eventVisibility),
            schema: { type: 'string' }
        } as IField);
        settingsFields.push({
            id: this.settingsPublishedFieldId, name: this.settingsPublishedFieldId, editable: false,
            label: this.sharedSvc.translate(TranslationKey.eventStatus),
            schema: { type: 'string' }
        } as IField);
        settingsFields.push({
            id: this.settingsLastRegistrationDateFieldId, name: this.settingsLastRegistrationDateFieldId, editable: false,
            label: this.sharedSvc.translate(TranslationKey.lastRegistrationDate),
            schema: { type: 'string', format: 'date-time' }
        } as IField);
        settingsFields.push({
            id: this.settingsPublicationDateFieldId, name: this.settingsPublicationDateFieldId, editable: false,
            label: this.sharedSvc.translate(TranslationKey.eventPublicationDate),
            schema: { type: 'string', format: 'date-time' }
        } as IField);
        settingsFields.push({
            id: this.settingsOfflineDateFieldId, name: this.settingsOfflineDateFieldId, editable: false,
            label: this.sharedSvc.translate(TranslationKey.eventOfflineDate),
            schema: { type: 'string', format: 'date-time' }
        } as IField);
        return settingsFields;
    }

    getEventCountersFields(): IField[] {
        const countersFields: IField[] = [];
        countersFields.push({
            id: this.eventCounterArrivedFieldName, name: this.eventCounterArrivedFieldName, editable: false,
            label: this.sharedSvc.translate(TranslationKey.arrived),
            schema: { type: 'integer' }
        } as IField);
        countersFields.push({
            id: this.eventCounterCancelledFieldName, name: this.eventCounterCancelledFieldName, editable: false,
            label: this.sharedSvc.translate(TranslationKey.cancelled),
            schema: { type: 'integer' }
        } as IField);
        countersFields.push({
            id: this.eventCounterRegisteredFieldName, name: this.eventCounterRegisteredFieldName, editable: false,
            label: this.sharedSvc.translate(TranslationKey.registered),
            schema: { type: 'integer' }
        } as IField);
        countersFields.push({
            id: this.eventCounterTotalFieldName, name: this.eventCounterTotalFieldName, editable: false,
            label: this.sharedSvc.translate(TranslationKey.totalParticipants),
            schema: { type: 'integer' }
        } as IField);
        return countersFields;
    }

    async getEventsThumbnailsFields(): Promise<IField[]> {
        const thumbnailsFieldNames = ['name', 'start', 'end', 'description', 'reference_number'];
        if (!this.globalEventFields) {
            await this.loadGlobalEventFields();
        }
        const thumbnailsFields = this.globalEventFields.filter(f => thumbnailsFieldNames.includes(f.name));
        return thumbnailsFields;
    }

    getEventTemplatesColumns(): IField[] {
        return [...this.globalEventFields, this.getCreatedAtField()];
    }

    getBuiltinGlobalFields(): IField[] {
        return this.globalEventFields.filter(x => x.builtin);
    }

    getTranslatedEventStatus(status: string): string {
        switch (status) {
            case 'no-registration':
                return this.sharedSvc.translate(TranslationKey.eventStatusNoRegistration);
            case 'published':
                return this.sharedSvc.translate(TranslationKey.eventStatusPublished);
            case 'off-line':
                return this.sharedSvc.translate(TranslationKey.eventStatusOffline);
            case 'cancelled':
                return this.sharedSvc.translate(TranslationKey.eventStatusCancelled);
            case 'approved':
                return this.sharedSvc.translate(TranslationKey.eventStatusApproved);
            case 'draft':
                return this.sharedSvc.translate(TranslationKey.eventStatusDraft);
        }
    }
}
